import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToast } from "src/componentes/Toast/Toast";
import {
  DestinatarioNFCeDto,
  MeioPagamentoNFCeDto,
  ProdutoConsultaDto,
  ProdutoConsultaSchema,
  ProdutoNFCeDto,
  NFCeDto,
  ProdutoConsultaComandoDto,
} from "./NFCe.Dto";
import { ProdutoFiltrar } from "src/services/Estoque.Service";
import { MapearErrosServidor } from "src/hook/Functions";
import {
  NFCeBuscarUltima,
  NFCeEnviar,
  NFCeIncluir,
} from "src/services/Fiscal.Service";
import NFCeMenuCode from "./NFCe.Menu.Code";
import NFCeConfiguracaoCode from "./NFCe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "../../Configuracoes/Sistema/Sistema.Dto";
import { NFCeCodeLoad } from "./NFCe.Code.Load";

const NFCeCode = () => {
  const [dataProdutoConsulta, setDataProdutoConsulta] = useState<
    ProdutoNFCeDto[]
  >([]);
  const [dataProdutoNFCe, setDataProdutoNFCe, CACHE_KEY] = NFCeCodeLoad();
  const [modalShowConsultaProduto, setModalShowConsultaProduto] =
    useState(false);
  const [modalShowFinaliza, setModalShowFinaliza] = useState(false);
  const [descricaoProdutoConsulta, setDescricaoProdutoConsulta] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataMeioPagamento, setDataMeioPagamento] = useState<
    MeioPagamentoNFCeDto[]
  >([]);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [produtoPropriedades, setProdutoPropriedades] =
    useState<ProdutoNFCeDto>();

  const { showToast } = useToast();
  const [nfceId, setNfceId] = useState(0);

  const {
    register: registerProdutoConsulta,
    handleSubmit: handleSubmitProdutoConsulta,
    formState: { errors: errorsProdutoConsulta },
    setFocus: setFocusProdutoConsulta,
    setValue: setValueProdutoConsulta,
    setError: setErrorProdutoConsulta,
  } = useForm<ProdutoConsultaDto>({
    resolver: yupResolver(ProdutoConsultaSchema),
  });

  const { imprimirDanfe, handleMenuClick, handleMenuToggleClick, menuItens } =
    NFCeMenuCode({ nfceId, setDescricaoProdutoConsulta });

  const { getConfiguracaoValor, loading: loadingConfiguracao } =
    NFCeConfiguracaoCode();

  useEffect(() => {
    if (loadingConfiguracao) return;
    const serie = Number(getConfiguracaoValor(TipoConfiguracaoEnum.Serie));
    if (!serie) return;

    const controller = new AbortController();
    const Load = async () => {
      setFocusProdutoConsulta("nome", { shouldSelect: true });
      setValueProdutoConsulta("nome", "");
      setLoading(true);

      await NFCeBuscarUltima(serie)
        .then((response) => {
          if (
            response.data.data.codigoStatusEnvio !== 2 &&
            response.data.data.codigoStatusEnvio !== 3
          ) {
            showToast({
              message: "Existe NFCe em aberto não enviadas",
              title: "Atenção",
              variant: "Warning",
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };
    Load();
    return () => {
      controller.abort();
    };
  }, [loadingConfiguracao, getConfiguracaoValor(TipoConfiguracaoEnum.Serie)]);

  const handleClickPropriedades = (value: ProdutoNFCeDto) => () => {
    setModalShowPropriedades(true);
    setProdutoPropriedades(value);
  };

  const onCancelModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = (value: ProdutoNFCeDto) => {
    if (dataProdutoNFCe.length === 1) {
      localStorage.setItem(CACHE_KEY, JSON.stringify([]));
      setDataProdutoNFCe([]);
    } else {
      setDataProdutoNFCe((prev) => prev.filter((item) => item !== value));
    }
  };

  const onSubmitProdutoConsulta = async (data: ProdutoConsultaDto) => {
    setLoading(true);

    const comando = ExtrairConsultaProdutoComando(data.nome);
    if (comando) {
      data.nome = comando.codigoProduto;
    }

    const response = await ProdutoFiltrar(data);
    if (response.data.success) {
      const data = response.data.data;

      data.forEach((item: ProdutoNFCeDto) => {
        item.valorUnitarioComercial =
          comando?.valor ?? item.valorUnitarioComercial;
        item.valorUnitarioTributavel = item.valorUnitarioComercial;
        item.quantidadeComercial = comando?.quantidade ?? 1;
        item.quantidadeTributavel = comando?.quantidade ?? 1;

        item.codigoBarrasTributavel = item.codigoBarras;
        item.unidadeTributavel = item.unidadeComercial;
        item.quantidadeTributavel = item.quantidadeComercial;

        item.indicaTotal = 1;
      });

      if (data.length === 0) {
        setErrorProdutoConsulta(
          "nome",
          { message: "Não localizado" },
          { shouldFocus: true }
        );
      } else if (data.length === 1) {
        setDescricaoProdutoConsulta(data[0].nome);
        const produtoNFCe = data[0] as ProdutoNFCeDto;
        setDataProdutoNFCe((prevData) => [...prevData, produtoNFCe]);
      } else {
        setDataProdutoConsulta(data);
        setModalShowConsultaProduto(true);
      }
    } else {
      MapearErrosServidor(response.data.notifications, showToast);
    }
    setLoading(false);
  };

  const onSubmitNFCe = async (value: DestinatarioNFCeDto | undefined) => {
    setLoading(true);
    const numeroAlterado = localStorage.getItem("nfceAlterarNumero");
    const nfce: NFCeDto = {
      serie: getConfiguracaoValor(TipoConfiguracaoEnum.Serie),
      numeroNF:
        numeroAlterado && !isNaN(Number(numeroAlterado))
          ? Number(numeroAlterado)
          : 0,
      destinatario: value,
      itens: dataProdutoNFCe,
      pagamento: dataMeioPagamento,
    };
    localStorage.removeItem("nfceAlterarNumero");
    showToast({
      message: "Inserindo...",
      title: "Em cadastro",
      variant: "Info",
    });
    const response = await NFCeIncluir(nfce);
    if (response.data.success) {
      showToast({
        message: "Aguarde...",
        title: "Enviando",
        variant: "Info",
      });
      const responseEnviar = await NFCeEnviar(response.data.data.id);
      if (responseEnviar.data.success) {
        showToast({
          message: responseEnviar.data.notifications[0].message,
          title: "Enviada",
          variant: "Primary",
        });
        if (
          responseEnviar.data.data?.codigoStatus &&
          responseEnviar.data.data?.codigoStatus === 100
        ) {
          imprimirDanfe(response.data.data.id);
          setNfceId(response.data.data.id);
          localStorage.setItem(CACHE_KEY, JSON.stringify([]));
          setDataProdutoNFCe([]);
          setDataMeioPagamento([]);
        }
      } else {
        response.data.notifications.forEach((item) => {
          showToast({
            message: item.message,
            title: "Erro",
            variant: "Danger",
          });
        });
      }
    } else {
      response.data.notifications.forEach((item) => {
        showToast({
          message: item.key + ": " + item.message,
          title: "Erro",
          variant: "Danger",
        });
      });
    }
    setLoading(false);
  };

  function ExtrairConsultaProdutoComando(
    input: string
  ): ProdutoConsultaComandoDto | null {
    const sanitized = input.trim().toLowerCase();

    // Expressão regular para capturar valor (v), quantidade (x) e código do produto.
    const regex =
      /^(?:(\d+(?:[.,]\d+)?)v)?(?:(\d+(?:[.,]\d+)?)x)?([a-z0-9]+)$/i;
    const match = sanitized.match(regex);

    if (!match) return null;

    let [, valorStr, quantidadeStr, codigoProduto] = match;

    // Substitui vírgula por ponto e converte para float
    const parseDecimal = (value: string) => Number(value.replace(",", "."));

    return {
      valor: valorStr ? parseDecimal(valorStr) : undefined,
      quantidade: quantidadeStr ? parseDecimal(quantidadeStr) : undefined,
      codigoProduto,
    };
  }

  const onHideModalConsultaProduto = () => {
    setModalShowConsultaProduto(false);
  };

  const onHideModalFinaliza = () => {
    setModalShowFinaliza(false);
  };

  const onSelectModalConsultaProduto = (value: ProdutoNFCeDto) => {
    onHideModalConsultaProduto();
    setFocusProdutoConsulta("nome", { shouldSelect: true });
    setDescricaoProdutoConsulta(value.nome);
    setDataProdutoNFCe((prevData) => [...prevData, value]);
  };

  const onSelectModalFinaliza = (value: MeioPagamentoNFCeDto) => {
    setDataMeioPagamento((prevData) => [...prevData, value]);
  };

  const totalNFCe = () => {
    const total = dataProdutoNFCe.reduce(
      (acc, item) =>
        acc + item.valorUnitarioComercial * item.quantidadeComercial,
      0
    );
    return total;
  };

  const possuiItens = () => {
    return dataProdutoNFCe.length > 0;
  };

  const onCancelModalFinaliza = () => {
    setDataMeioPagamento([]);
  };

  return {
    loading,
    onSubmitProdutoConsulta,
    handleSubmitProdutoConsulta,
    errorsProdutoConsulta,
    registerProdutoConsulta,
    modalShowConsultaProduto,
    dataProdutoConsulta,
    onHideModalConsultaProduto,
    onSelectModalConsultaProduto,
    onCancelModalFinaliza,
    dataProdutoNFCe,
    totalNFCe,
    descricaoProdutoConsulta,
    setFocusProdutoConsulta,
    setValueProdutoConsulta,
    possuiItens,
    modalShowFinaliza,
    setModalShowFinaliza,
    dataMeioPagamento,
    onSelectModalFinaliza,
    onHideModalFinaliza,
    onSubmitNFCe,
    modalShowPropriedades,
    setModalShowPropriedades,
    handleClickPropriedades,
    produtoPropriedades,
    onCancelModalPropriedades,
    onDeleteModalPropriedades,
    handleMenuClick,
    menuItens,
    handleMenuToggleClick,
  };
};
export default NFCeCode;
